interface LocalStorageItem {
  expiry: number;
  ttl: number;
  value: unknown;
}

export class ManagedLocalStorage {
  private readonly prefix = "yt_managed_";

  constructor(
    private readonly rawKey: string,
    maxItems: number = 20,
  ) {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(this.prefix),
    );

    const allItems = keys.map((key) => ({
      item: JSON.parse(localStorage.getItem(key) as string) as LocalStorageItem,
      key,
    }));

    const validItems: Array<{ item: LocalStorageItem; key: string }> = [];
    // Dropping stale items
    for (const v of allItems) {
      if (Date.now() >= v.item.expiry) {
        localStorage.removeItem(v.key);
      } else {
        validItems.push(v);
      }
    }

    // Sorting from oldest to newest
    validItems.sort((a, b) => a.item.expiry - b.item.expiry);

    // Remove excess items
    if (validItems.length > maxItems) {
      validItems.slice(0, validItems.length - maxItems).forEach(({ key }) => {
        localStorage.removeItem(key);
      });
    }
  }

  getItem<T>() {
    const itemStr = localStorage.getItem(this.key);
    if (!itemStr) return null;

    const item: LocalStorageItem = JSON.parse(itemStr);

    // Check if the item has expired
    if (Date.now() > item.expiry) {
      localStorage.removeItem(this.key);
      return null;
    }

    // Refresh expiry time
    this.setItem(item.value, item.ttl);
    return item.value as T | null;
  }

  removeItem() {
    localStorage.removeItem(this.key);
  }

  setItem<T>(value: T, ttlInDays: number): void {
    if (!value) {
      this.removeItem();
      return;
    }

    const ttl = ttlInDays * 1000 * 60 * 60 * 24;
    const expiry = Date.now() + ttl;
    const item: LocalStorageItem = { expiry, ttl, value };
    localStorage.setItem(this.key, JSON.stringify(item));
  }

  get key() {
    return this.prefix + this.rawKey;
  }
}
